'use client';
// lib/hooks/useTracking.ts
import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/lib/store';
import { trackPageVisit } from '@/lib/store/slices/trackingSlice';
import {
    collectDeviceInfo,
    extractUtmParams,
    getOrCreateSessionId,
    getPageLoadTime,
    getScrollDepth,
    getStoredVisitorId,
    patchPageView,
    saveVisitorId,
} from '@/lib/utils/tracking';

export function useTracking() {
    const dispatch   = useDispatch<AppDispatch>();
    const visitorId  = useSelector((s: RootState) => s.tracking.visitorId);
    const pathname   = usePathname();

    // Refs live across renders without causing re-renders
    const pageViewIdRef = useRef<string | null>(null);
    const startTimeRef  = useRef<number>(Date.now());
    const maxScrollRef  = useRef<number>(0);
    const sentRef       = useRef<boolean>(false); // prevent double-send on unload + cleanup

    // ── Helper: send accumulated metrics ─────────────────────────────────
    const sendMetrics = (isExit: boolean) => {
        const pageViewId = pageViewIdRef.current;
        if (!pageViewId || sentRef.current) return;
        sentRef.current = true;

        const timeOnPage = Math.round((Date.now() - startTimeRef.current) / 1000);
        patchPageView(pageViewId, {
            timeOnPage,
            scrollDepth: maxScrollRef.current,
            isExit,
        });
    };

    // ── Track page visit on every route change ────────────────────────────
    useEffect(() => {
        // Reset state for the new page
        pageViewIdRef.current = null;
        startTimeRef.current  = Date.now();
        maxScrollRef.current  = getScrollDepth();
        sentRef.current       = false;

        // Small delay so the browser has time to finish navigation timing metrics
        const timer = setTimeout(async () => {
            try {
                const result = await dispatch(
                    trackPageVisit({
                        visitorId: visitorId ?? getStoredVisitorId() ?? undefined,
                        sessionId: getOrCreateSessionId(),
                        page:      pathname,
                        title:     document.title,
                        referrer:  document.referrer || undefined,
                        loadTime:  getPageLoadTime(),
                        utmParams: extractUtmParams(),
                        ...collectDeviceInfo(),
                    })
                ).unwrap();

                pageViewIdRef.current = result.pageViewId;
                saveVisitorId(result.visitorId);
            } catch {
                // Tracking is non-critical — never surface errors to the user
            }
        }, 300);

        // Send metrics for the *previous* page when route changes
        return () => {
            clearTimeout(timer);
            sendMetrics(false);
        };
    }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Scroll depth tracking ─────────────────────────────────────────────
    useEffect(() => {
        const onScroll = () => {
            const depth = getScrollDepth();
            if (depth > maxScrollRef.current) maxScrollRef.current = depth;
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // ── beforeunload: real browser close / refresh / external navigation ──
    useEffect(() => {
        const onUnload = () => sendMetrics(true);
        window.addEventListener('beforeunload', onUnload);
        return () => window.removeEventListener('beforeunload', onUnload);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
